import loadable from '@loadable/component';
import React, { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import useResponsiveEffect from '../../../../hooks/useResponsiveEffect';
import { Toaster } from '../../../ui';
import { ShoppingTagIcon } from '../../../ui/Icons';
import HomeHero from '../../../consumer/HomeHero/HomeHeroContainer';
import { SearchSubmitter } from '../../../consumer/search/SearchPopover/types';
import SearchTileGridContainer from '../SearchTileGrid/SearchTileGridContainer';
import SEOLinksContainer from '../SEOLinks/SEOLinksContainer';
import SEOTags from '../SEOTags';
import { AppointmentOverviewToday } from '../../../../api/Users/Appointments/AppointmentsToday';
import { useQueryParams } from '../../../../hooks';
import { useReferralEntrypointProps } from '../../../consumer/referrals/useReferralEntrypointProps';
import { ShareTarget } from '../../../../modules/share';
import { SearchPopoverModal } from '../../../consumer/search/SearchPopover';
import { useShowReferralLandingIfNeeded } from '../../../consumer/referrals/useShowReferralLandingIfNeeded';
import { generatePlatformMessage } from '../../../../modules/referral/helpers';
import { IncentivesBanner } from '../../../consumer/incentives';
import useDiscountDetails from '../../../consumer/incentives/useDiscountDetails';
import { AboutDiscountsModalGotItCta } from '../../../consumer/incentives/AboutDiscountsModal/AboutDiscountsModalGotItCta';
import { MarketingVideos } from '../../../ui/MarketingVideos';
import { KlarnaLearnMoreHero, useIsKlarnaHomePageHeroEnabled } from '../../../consumer/klarna/KlarnaLearnMoreHero';

import { State } from '../../../../store/RouteState.model';
import { State as searchState } from '../../../../store/Search.model';
import { SEARCH_ROUTE } from '../../../../route-names';
import type { IRootDispatch, RootState } from '../../../../store/models';
import store from '../../../../store';
import { JoinEntryContainer } from '../JoinEntry';

const UpcomingAppointmentCard = loadable(() => (
  import('../../../consumer/appointments/UpcomingAppointmentCard')
));

const CurrentAppointmentCard = loadable(() => (
  import('../../../consumer/appointments/CurrentAppointmentCard')
));

const FavoritesCarouselContainer = loadable(() => (
  import('../../../consumer/favorites/FavoritesCarousel/FavoritesCarouselContainer')
));

const PressQuotes = loadable(() => (
  import('../PressQuotes')
));

const ClientReferralHero = loadable(() => (
  import('../../../consumer/referrals/entrypoints/ClientReferralHero')
), { resolveComponent: mod => mod.ClientReferralHero });

const ShareTrigger = loadable(() => (
  import('../../../consumer/sharing')
), { resolveComponent: mod => mod.ShareTrigger });

export type HomePageProps = {
  /** True if the user is logged in, otherwise false */
  isLoggedIn: boolean;
  /** Object of utms */
  utmParams: Record<string, string>;
  isPromoModEnabled: boolean;
  onIncentivesSearch: SearchSubmitter;
  upcomingAppt: AppointmentOverviewToday | null;
  currentAppt: AppointmentOverviewToday | null;
  isLandingRedesignEnabled?: boolean;
};

export const HomePage = ({
  isLoggedIn,
  utmParams = {},
  onIncentivesSearch,
  upcomingAppt,
  currentAppt,
}: HomePageProps) => {
  const dispatch = useDispatch<IRootDispatch>();
  const [isSearchPopoverOpen, setIsSearchPopoverOpen] = useState<boolean>(false);
  const is = useResponsiveEffect();
  const route = useSelector<RootState, State>(state => state.route);
  const search = useSelector<RootState, searchState>(state => state.search);

  useEffect(() => {
    if (route.previous?.name === SEARCH_ROUTE && search.clickedBackButton) {
      setIsSearchPopoverOpen(true);
      dispatch.search.clearClickedBackButton();
    }
  }, [
    route.previous,
    search?.clickedBackButton,
    dispatch?.search,
  ]);

  const userSelector = store.select.user;
  const isLandingRedesignEnabled = useSelector(userSelector.inLandingRedesign2024);

  const referralShareProps = useReferralEntrypointProps({
    message: generatePlatformMessage,
    target: ShareTarget.PLATFORM,
    location: 'referrals_homepage',
  });

  useShowReferralLandingIfNeeded(
    { target: ShareTarget.PLATFORM },
    {
      view_id: 'referrals_homepage_popover_viewed',
      click_id: 'referrals_homepage_popover_click',
      dismiss_id: 'referrals_homepage_popover_dismiss',
    },
    () => setIsSearchPopoverOpen(true),
  );

  const handleSearchPopoverDismiss = () => {
    setIsSearchPopoverOpen(false);
  };

  const ncdAndDiscountedPros = useDiscountDetails();

  const query = useQueryParams();
  const showInvalidToaster = query.get('discount_invalid');
  const showReferralHero = (!isLoggedIn || is.desktop) && !!referralShareProps;
  const showKlarnaHero = useIsKlarnaHomePageHeroEnabled();
  const layoutWidth = 1440;

  return (
    <React.Fragment>
      <Box
        maxWidth={layoutWidth}
        style={{
          width: '100%',
        }}
      >
        {ncdAndDiscountedPros?.discountCode && (
          <IncentivesBanner
            discountDetails={ncdAndDiscountedPros}
            renderMoreDetailsCta={
                onDismiss => (<AboutDiscountsModalGotItCta onDismiss={onDismiss} />)
              }
          />
        )}
        {isLoggedIn && !is.desktop && currentAppt && (
          <CurrentAppointmentCard
            appointment={currentAppt}
            provider={currentAppt.provider}
            card={currentAppt.client_card_on_file}
            source="client_homepage"
          />
        )}
        {isLoggedIn && !is.desktop && !currentAppt && upcomingAppt && (
          // for now, ensure we are only showing one card at a time
          // current takes priority
          <UpcomingAppointmentCard
            appointment={upcomingAppt}
            provider={upcomingAppt.provider}
            card={upcomingAppt.client_card_on_file}
            source="client_homepage"
          />
        )}
        {/** only show for mobile users */}
        {isLoggedIn && !is.desktop && (
          <FavoritesCarouselContainer />
        )}
        <HomeHero
          // @ts-ignore
          utmParams={utmParams}
          isLandingRedesignEnabled={isLandingRedesignEnabled}
        />
        <Box>
          <SearchTileGridContainer params={utmParams} />
        </Box>
        {
          isLandingRedesignEnabled && (<JoinEntryContainer />)
        }

        <Box padding={8}>
          <MarketingVideos slotName="homepage" />
        </Box>

        {showKlarnaHero && (
          <Box paddingX={16} paddingBottom={32}>
            <KlarnaLearnMoreHero />
          </Box>
        )}
        {(showReferralHero && !!referralShareProps) && (
          <Box paddingX={showReferralHero ? 16 : undefined}>
            <ShareTrigger
              {...referralShareProps}
              renderTrigger={onClick => (
                <ClientReferralHero onClick={onClick} />
              )}
            />
          </Box>
        )}
        {isSearchPopoverOpen && (
          <SearchPopoverModal
            showIncentivesStar
            source="platform_referral"
            onSubmit={onIncentivesSearch}
            onClose={handleSearchPopoverDismiss}
          />
        )}
        <PressQuotes />
        {Platform.OS === 'web' ? (
          <SEOLinksContainer
            utmParams={utmParams}
            clearCoordinates={() => dispatch.search.clearCoordinates()}
          />
        ) : null}
        <SEOTags />
      </Box>
      {showInvalidToaster && (
        <Toaster
          icon={<ShoppingTagIcon color={BrandColor.Yellow} />}
          boxColor={BrandColor.Yellow}
          isLoggedIn={isLoggedIn}
          title="Looks like this promotion has expired"
          content="This promotion is no longer available to use"
        />
      )}
    </React.Fragment>
  );
};

export default HomePage;
